import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ProjectCard from './ProjectCard';
import { client } from '../lib/sanity';
import staticProjects from '../data/projects';

const PROJECTS_PER_PAGE = 6;

// GROQ query to fetch projects from Sanity
const PROJECTS_QUERY = `*[_type == "caseStudy"] | order(coalesce(sortOrder, 100) asc, publishedAt desc) {
  _id,
  title,
  "slug": slug.current,
  description,
  category,
  thumbnail,
  year,
  sortOrder
}`;

export const ProjectGrid = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [projects, setProjects] = useState(staticProjects);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await client.fetch(PROJECTS_QUERY);
        if (data && data.length > 0) {
          setProjects(data);
        }
        // If no Sanity data, keep using static projects
      } catch (error) {
        console.log('Using static projects (Sanity not configured yet)');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const totalPages = Math.ceil(projects.length / PROJECTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
  const currentProjects = projects.slice(startIndex, startIndex + PROJECTS_PER_PAGE);

  const goToPage = (page) => {
    setCurrentPage(page);
    const workSection = document.getElementById('work');
    if (workSection) {
      workSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="work" 
      className="py-24 lg:py-32 px-6 lg:px-12"
      data-testid="work-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="font-heading text-4xl lg:text-5xl text-foreground-primary tracking-tight">
            Case Studies
          </h2>
          <p className="font-body text-foreground-secondary mt-4 max-w-lg">
            A collection of projects spanning product design, UX research, and digital experiences.
          </p>
        </motion.div>

        {/* Loading state */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Projects Grid */}
        {!loading && (
          <div 
            className="grid md:grid-cols-2 gap-12 lg:gap-16"
            data-testid="projects-grid"
          >
            {currentProjects.map((project, index) => (
              <ProjectCard
                key={project._id || project.id}
                project={project}
                index={index}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <motion.div 
            className="flex items-center justify-center gap-4 mt-16 lg:mt-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-10 h-10 rounded-full border border-border-subtle flex items-center justify-center hover:bg-accent-light hover:border-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-4 h-4 text-foreground-primary" strokeWidth={1.5} />
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`w-10 h-10 rounded-full font-body text-sm transition-all duration-200 ${
                    currentPage === page
                      ? 'bg-foreground-primary text-background-primary'
                      : 'hover:bg-accent-light text-foreground-secondary'
                  }`}
                  data-testid={`pagination-page-${page}`}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-10 h-10 rounded-full border border-border-subtle flex items-center justify-center hover:bg-accent-light hover:border-accent disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200"
              data-testid="pagination-next"
            >
              <ChevronRight className="w-4 h-4 text-foreground-primary" strokeWidth={1.5} />
            </button>
          </motion.div>
        )}

        {/* Page indicator */}
        {!loading && totalPages > 1 && (
          <p className="text-center font-body text-sm text-foreground-secondary mt-4">
            Page {currentPage} of {totalPages}
          </p>
        )}
      </div>
    </section>
  );
};

export default ProjectGrid;
